import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, Spinner } from "@/components/shared";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, ShoppingBag, User, BadgeCheck, Sparkles } from "lucide-react";

const CAT_LABELS = {
  histoire: "📚 Histoire", romans: "📖 Romans", jeunesse: "👦 Jeunesse", parents: "👨‍👩‍👧 Parents",
  psychologie: "🧠 Psychologie", decolonisation: "✊🏿 Décolonisation", afrique: "🌍 Afrique", esclavage: "📜 Esclavage et mémoire",
};

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Container><Spinner label="Chargement du livre..." /></Container>;
  if (!book) return <Container><p className="text-white/50">Livre introuvable.</p></Container>;

  const primary = (book.buttons || []).filter((b) => b.kind === "primary");
  const secondary = (book.buttons || []).filter((b) => b.kind !== "primary");

  return (
    <Container className="max-w-5xl">
      <button data-testid="book-back" onClick={() => navigate("/bibliotheque")}
        className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Bibliothèque</button>

      <div className="grid md:grid-cols-[300px_1fr] gap-8 lg:gap-12">
        {/* 1. Couverture */}
        <div>
          <div className="sticky top-6">
            <div className="overflow-hidden rounded-2xl border border-white/10 aspect-[2/3] bg-white/[0.03]" data-testid="book-cover">
              {book.cover_url
                ? <img src={book.cover_url} alt={book.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full" />}
            </div>
            {/* 10. Boutons (visibles aussi sous la couverture sur desktop) */}
            <div className="hidden md:flex flex-col gap-2 mt-4">
              <ActionButtons primary={primary} secondary={secondary} />
            </div>
          </div>
        </div>

        <div>
          {/* 2. Titre / 3. Autrice */}
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-tight" data-testid="book-title">{book.title}</h1>
          <p className="text-white/60 text-lg mt-2">{book.author}</p>

          {/* 4. Badge Autrice partenaire */}
          {book.badge && (
            <div className="inline-flex items-center gap-2 mt-4 rounded-full border border-gold/40 bg-gold/[0.08] px-3 py-1.5" data-testid="book-badge">
              <BadgeCheck size={15} className="text-gold" />
              <span className="text-gold text-sm font-medium">{book.badge}</span>
            </div>
          )}

          {/* 5. Catégories */}
          <div className="flex flex-wrap gap-2 mt-5" data-testid="book-categories">
            {(book.categories || []).map((c) => (
              <span key={c} className="text-xs rounded-full bg-white/[0.05] border border-white/10 px-2.5 py-1 text-white/70">{CAT_LABELS[c] || c}</span>
            ))}
          </div>

          {/* 6. Résumé */}
          {book.summary && (
            <section className="mt-8">
              <h2 className="font-display text-xl mb-2">Résumé</h2>
              <p className="text-white/70 leading-relaxed">{book.summary}</p>
            </section>
          )}

          {/* 7. Pourquoi BIZARRE AI recommande ce livre */}
          {book.why && (
            <section className="mt-8 card-surface noise p-6 border-gold/20" data-testid="book-why">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={17} className="text-gold" />
                <h2 className="font-display text-lg gold-text">Pourquoi BIZARRE AI recommande ce livre</h2>
              </div>
              <p className="text-white/75 leading-relaxed italic">{book.why}</p>
            </section>
          )}

          {/* 8. Prix / ISBN / date */}
          <section className="mt-8 grid grid-cols-3 gap-3">
            {book.price && <Meta label="Prix" value={book.price} />}
            {book.isbn && <Meta label="ISBN" value={book.isbn} />}
            {book.published_at && <Meta label="Publié le" value={book.published_at} />}
          </section>

          {/* 9. Biographie */}
          {book.biography && (
            <section className="mt-8">
              <h2 className="font-display text-xl mb-2 flex items-center gap-2"><User size={18} className="text-gold" /> À propos de l'autrice</h2>
              <p className="text-white/65 leading-relaxed text-[15px]">{book.biography}</p>
            </section>
          )}

          {/* 10. Boutons (mobile) */}
          <div className="flex flex-col gap-2 mt-8 md:hidden">
            <ActionButtons primary={primary} secondary={secondary} />
          </div>
        </div>
      </div>
    </Container>
  );
}

const Meta = ({ label, value }) => (
  <div className="card-surface p-3 text-center">
    <p className="text-white/40 text-[10px] uppercase tracking-[0.15em]">{label}</p>
    <p className="text-white/90 text-sm mt-1 font-medium break-words">{value}</p>
  </div>
);

const ActionButtons = ({ primary, secondary }) => (
  <>
    {primary.map((b, i) => (
      <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`book-btn-primary-${i}`}
        className="gold-gradient text-black rounded-full px-5 py-3.5 font-semibold text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
        <ShoppingBag size={16} /> {b.label}
      </a>
    ))}
    {secondary.map((b, i) => (
      <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`book-btn-secondary-${i}`}
        className="border border-white/20 text-white rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 hover:border-gold/40 hover:text-gold transition-colors">
        <ExternalLink size={15} /> {b.label}
      </a>
    ))}
  </>
);
